import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    assetLabel,
    distributed,
    draftInvestment,
    emptyDistribution,
    emptyInvestment,
    emptyRound,
    fromDraft,
    invested,
    pnlOf,
    pnlPctOf,
    sectorLabel,
    sortInvestments,
    toDraft,
} from './privateMaths';
import { formatPct } from './timeframes';
import PositionsTable from './PositionsTable';
import { useT } from '../../i18n';

// One section of the private investments — Active or Exits — as a table.
//
// One row per INVESTMENT. Its P/L is told in three columns, each kept as
// entries: the initial cost basis (every cheque in), what came back (every
// distribution), and the one unrealised mark. The first two show their USD
// total, with the entries behind it opening beneath the row. Every column is
// editable in place from the pencil at the row's end, entries included, and a
// row can be moved to the other section without editing it.

const COLUMNS = [
    { key: 'name', labelKey: 'private.col.name', align: 'left' },
    { key: 'kind', labelKey: 'private.col.kind', align: 'left' },
    { key: 'sector', labelKey: 'private.col.sector', align: 'left' },
    { key: 'invested', labelKey: 'private.col.invested', align: 'right', numeric: true },
    { key: 'distributed', labelKey: 'private.col.distributed', align: 'right', numeric: true },
    // An exit holds nothing, so it has no mark to show.
    { key: 'currentMark', labelKey: 'private.col.mark', align: 'right', numeric: true, activeOnly: true },
    // Everything held or returned, less what went in: the total while held,
    // all of it realised once exited.
    { key: 'pnl', labelKey: 'private.col.pnl', exitLabelKey: 'private.col.realised', align: 'right', numeric: true },
    { key: 'comments', labelKey: 'private.col.comments', align: 'left', wide: true },
];

// The entries behind a column. `asset` is a pair — quantity and symbol, read
// together as "25 ETH" — stored as `assetQuantity` and `asset`.
const ENTRY_LISTS = {
    rounds: {
        titleKey: 'private.col.invested',
        ariaKey: 'private.round.aria',
        addKey: 'private.round.add',
        removeAriaKey: 'private.round.removeAria',
        empty: emptyRound,
        fields: [
            { key: 'round', labelKey: 'private.round.description', type: 'text', max: 60 },
            { key: 'investedOn', labelKey: 'private.round.date', type: 'date' },
            { key: 'amountUsd', labelKey: 'private.round.amount', type: 'amount' },
            { key: 'asset', labelKey: 'private.round.asset', type: 'asset' },
            { key: 'valuationUsd', labelKey: 'private.round.valuation', type: 'amount', missingKey: 'private.na' },
            { key: 'units', labelKey: 'private.round.units', type: 'count' },
        ],
    },
    distributions: {
        titleKey: 'private.col.distributed',
        ariaKey: 'private.dist.aria',
        addKey: 'private.dist.add',
        removeAriaKey: 'private.dist.removeAria',
        empty: emptyDistribution,
        fields: [
            { key: 'description', labelKey: 'private.dist.description', type: 'text', max: 120 },
            { key: 'receivedOn', labelKey: 'private.dist.date', type: 'date' },
            { key: 'amountUsd', labelKey: 'private.dist.amount', type: 'amount' },
            { key: 'asset', labelKey: 'private.dist.asset', type: 'asset' },
            { key: 'sold', labelKey: 'private.dist.sold', type: 'yesNo' },
            { key: 'soldInto', labelKey: 'private.dist.soldInto', type: 'text', max: 30 },
        ],
    },
};

const isNumeric = (f) => f.type === 'amount' || f.type === 'count';

const KINDS = ['VC', 'DAO', 'Fund', 'Real Estate'];

function PencilIcon() {
    return (
        <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true" focusable="false">
            <path d="M11.2 2.3l2.5 2.5-8.4 8.4-3.1.6.6-3.1z" />
            <path d="M9.6 3.9l2.5 2.5" />
        </svg>
    );
}

/** Whole dollars: "$100,000". The figures here are cheques, not quotes. */
const useMoney = () => {
    const { fmt } = useT();
    return (v) => (v === null || v === undefined || v === '' ? null : `$${fmt.number(v, 0)}`);
};

/** A token or share count as typed — "1,200", "0.5" — with no decimals
 *  added or rounded away. */
const useQuantity = () => {
    const { fmt } = useT();
    return (v) => {
        const decimals = (String(v).split('.')[1] || '').length;
        return fmt.number(v, Math.min(decimals, 8));
    };
};

/** "+$140,249" / "−$12,000", the minus being U+2212 like the rest of the
 *  portfolio's signed figures. */
const useSignedMoney = () => {
    const { fmt } = useT();
    return (v) => {
        if (v === null || v === undefined) return null;
        const body = `$${fmt.number(Math.abs(v), 0)}`;
        return v > 0 ? `+${body}` : v < 0 ? `−${body}` : body;
    };
};

function PnlCell({ pnl, pct }) {
    const signedMoney = useSignedMoney();
    const tone = pnl === null || pnl === 0 ? '' : pnl < 0 ? ' is-negative' : ' is-positive';
    if (pnl === null) return <td className="is-right"><span className="positions__empty">—</span></td>;
    return (
        <td className={`is-right${tone}`}>
            {signedMoney(pnl)}
            {formatPct(pct, 1) && <span className="private__pct">{formatPct(pct, 1)}</span>}
        </td>
    );
}

/** "$268,884 | 40 ETH" — the mark in dollars, then in its own denomination. */
function MarkValue({ investment }) {
    const money = useMoney();
    const quantity = useQuantity();
    const usd = money(investment.currentMark);
    const native = assetLabel(investment.markQuantity, investment.markAsset, quantity);
    if (!usd && !native) return <span className="positions__empty">—</span>;
    return (
        <>
            {usd ?? '—'}
            {native && <span className="private__denom"> | {native}</span>}
        </>
    );
}

/** A column's USD total, and the button that opens the entries behind it.
 *  Named with its column: a row has two of these, both reading "1 entry". */
function TotalWithEntries({ titleKey, total, count, expanded, onToggle }) {
    const { t, tn } = useT();
    const money = useMoney();
    return (
        <>
            {count > 0 ? money(total) : <span className="positions__empty">—</span>}
            {count > 0 && (
                <button
                    type="button"
                    className="private__rounds_toggle"
                    aria-expanded={expanded}
                    aria-label={`${t(titleKey)}: ${tn('private.entries', count)}`}
                    onClick={onToggle}
                >
                    {tn('private.entries', count)}
                    <span aria-hidden="true">{expanded ? ' ▴' : ' ▾'}</span>
                </button>
            )}
        </>
    );
}

function Entries({ list: listKey, entries, draft, onChange, onAdd, onRemove }) {
    const { t, fmt } = useT();
    const money = useMoney();
    const quantity = useQuantity();
    const spec = ENTRY_LISTS[listKey];
    const editing = Boolean(draft);
    const list = editing ? draft[listKey] : entries;

    const view = (f, e) => {
        switch (f.type) {
            case 'date': return fmt.date(e[f.key]) ?? '—';
            case 'amount': return money(e[f.key]) ?? (f.missingKey ? t(f.missingKey) : '—');
            case 'count': return e[f.key] === null || e[f.key] === undefined ? '—' : quantity(e[f.key]);
            case 'asset': return assetLabel(e.assetQuantity, e.asset, quantity) ?? '—';
            case 'yesNo': return e.sold === true ? t('private.yes') : e.sold === false ? t('private.no') : '—';
            default: return e[f.key] || '—';
        }
    };

    const input = (f, e, i) => {
        const label = t(spec.ariaKey, { field: t(f.labelKey), n: i + 1 });
        const set = (key) => (ev) => onChange(i, key, ev.target.value);
        switch (f.type) {
            case 'asset':
                return (
                    <span className="private__asset">
                        <input
                            className="private__input is-right"
                            inputMode="decimal"
                            value={e.assetQuantity}
                            onChange={set('assetQuantity')}
                            placeholder={t('private.quantityPlaceholder')}
                            aria-label={t('private.quantityAria', { field: label })}
                        />
                        <input
                            className="private__input"
                            value={e.asset}
                            onChange={set('asset')}
                            placeholder={t('private.assetPlaceholder')}
                            aria-label={label}
                            maxLength={30}
                        />
                    </span>
                );
            case 'yesNo':
                return (
                    <select className="themes__select" value={e.sold} onChange={set('sold')} aria-label={label}>
                        <option value="">—</option>
                        <option value="Y">{t('private.yes')}</option>
                        <option value="N">{t('private.no')}</option>
                    </select>
                );
            default:
                return (
                    <input
                        type={f.type === 'date' ? 'date' : 'text'}
                        inputMode={isNumeric(f) ? 'decimal' : undefined}
                        value={e[f.key]}
                        onChange={set(f.key)}
                        aria-label={label}
                        maxLength={f.max}
                        // Nothing to sell into until it is marked as sold.
                        disabled={f.key === 'soldInto' && e.sold !== 'Y' && !e.soldInto}
                        className={`private__input${isNumeric(f) ? ' is-right' : ''}`}
                    />
                );
        }
    };

    return (
        <div className="private__rounds">
            <div className="private__entries_h">{t(spec.titleKey)}</div>
            {list.length > 0 && (
                <table className="private__rounds_table">
                    <thead>
                        <tr>
                            {spec.fields.map((f) => (
                                <th key={f.key} className={isNumeric(f) ? 'is-right' : 'is-left'}>{t(f.labelKey)}</th>
                            ))}
                            {editing && <th aria-hidden="true" />}
                        </tr>
                    </thead>
                    <tbody>
                        {list.map((e, i) => (
                            // Index keys are right here: entries have no
                            // identity of their own and are saved as an
                            // ordered set.
                            // eslint-disable-next-line react/no-array-index-key
                            <tr key={i}>
                                {spec.fields.map((f) => (
                                    <td key={f.key} className={isNumeric(f) ? 'is-right' : 'is-left'}>
                                        {editing ? input(f, e, i) : view(f, e)}
                                    </td>
                                ))}
                                {editing && (
                                    <td className="is-right">
                                        <button
                                            type="button"
                                            className="notes__link is-danger"
                                            onClick={() => onRemove(i)}
                                            aria-label={t(spec.removeAriaKey, { n: i + 1 })}
                                        >
                                            {t('private.removeRound')}
                                        </button>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
            {editing && (
                <button type="button" className="notes__link" onClick={onAdd}>{t(spec.addKey)}</button>
            )}
        </div>
    );
}

function Editor({ draft, setDraft, columns, onSave, onCancel, onDelete, busy, error }) {
    const { t } = useT();
    const money = useMoney();
    const signedMoney = useSignedMoney();
    const set = (key) => (e) => setDraft({ ...draft, [key]: e.target.value });
    const entriesProps = (listKey) => ({
        list: listKey,
        draft,
        onChange: (i, key, value) => setDraft({
            ...draft,
            [listKey]: draft[listKey].map((e, n) => (n === i ? { ...e, [key]: value } : e)),
        }),
        onAdd: () => setDraft({ ...draft, [listKey]: [...draft[listKey], ENTRY_LISTS[listKey].empty()] }),
        onRemove: (i) => setDraft({ ...draft, [listKey]: draft[listKey].filter((_, n) => n !== i) }),
    });

    // The totals and the P/L are never typed: they are the entries, summed,
    // as the draft stands.
    const current = draftInvestment(draft);

    const cell = (key) => {
        switch (key) {
            case 'name':
                return <input className="private__input" value={draft.name} onChange={set('name')} aria-label={t('private.col.name')} autoFocus maxLength={120} />;
            case 'kind':
                return (
                    <select className="themes__select" value={draft.kind} onChange={set('kind')} aria-label={t('private.col.kind')}>
                        {KINDS.map((k) => <option key={k} value={k}>{t(`private.kinds.${k}`)}</option>)}
                    </select>
                );
            case 'sector':
                return (
                    <span className="private__pair">
                        <input className="private__input" value={draft.sector} onChange={set('sector')} placeholder={t('private.sectorPlaceholder')} aria-label={t('private.sectorAria')} maxLength={60} />
                        <input className="private__input" value={draft.subSector} onChange={set('subSector')} placeholder={t('private.subSectorPlaceholder')} aria-label={t('private.subSectorAria')} maxLength={60} />
                    </span>
                );
            case 'invested':
                return <span className="private__computed">{money(invested(current))}</span>;
            case 'distributed':
                return <span className="private__computed">{money(distributed(current))}</span>;
            case 'currentMark':
                // Live from the broker: nothing to type.
                if (draft.linkedAccountId) return <span className="private__computed">{money(current.currentMark)}</span>;
                return (
                    <span className="private__pair">
                        <input className="private__input is-right" inputMode="decimal" value={draft.currentMark} onChange={set('currentMark')} placeholder={t('private.usdPlaceholder')} aria-label={t('private.col.mark')} />
                        <span className="private__asset">
                            <input className="private__input is-right" inputMode="decimal" value={draft.markQuantity} onChange={set('markQuantity')} placeholder={t('private.quantityPlaceholder')} aria-label={t('private.markQuantityAria')} />
                            <input className="private__input" value={draft.markAsset} onChange={set('markAsset')} placeholder={t('private.assetPlaceholder')} aria-label={t('private.markAssetAria')} maxLength={30} />
                        </span>
                    </span>
                );
            case 'pnl': {
                const pnl = pnlOf(current);
                return <span className="private__computed">{pnl === null ? '—' : signedMoney(pnl)}</span>;
            }
            case 'comments':
                return <textarea className="private__input private__comments_input" value={draft.comments} onChange={set('comments')} aria-label={t('private.col.comments')} rows={2} maxLength={2000} />;
            default:
                return null;
        }
    };

    return (
        <>
            <tr className="private__row is-editing">
                {columns.map((c) => (
                    <td key={c.key} className={`is-${c.align}`}>{cell(c.key)}</td>
                ))}
                <td className="private__actions">
                    <div className="private__stack">
                        <button type="button" className="settings__rename_go" onClick={onSave} disabled={busy}>{t('common.save')}</button>
                        <button type="button" className="settings__rename_go" onClick={onCancel}>{t('common.cancel')}</button>
                    </div>
                </td>
            </tr>
            <tr className="private__detail">
                <td colSpan={columns.length + 1}>
                    <Entries {...entriesProps('rounds')} />
                    <Entries {...entriesProps('distributions')} />
                    {error && <p className="holdings__error" role="alert">{t(`private.errors.${error}`)}</p>}
                    {/* Down here rather than beside save: it is the one
                        destructive control, and the row is already wide. */}
                    {onDelete && (
                        <button type="button" className="notes__link is-danger private__delete" onClick={onDelete} disabled={busy}>
                            {t('private.delete')}
                        </button>
                    )}
                </td>
            </tr>
        </>
    );
}

export default function PrivateTable({ investments, status, onSave, onMove, onDelete, busy, baseCurrency = 'USD' }) {
    const { t, tn } = useT();
    const [sort, setSort] = useState({ key: 'invested', direction: 'desc' });
    // Which entry lists are open, as `${id}:rounds` / `${id}:distributions`.
    const [open, setOpen] = useState(() => new Set());
    // Which row is being edited ('new' for an added one), and its draft.
    const [editing, setEditing] = useState(null);
    const [draft, setDraft] = useState(null);
    const [error, setError] = useState(null);

    const columns = COLUMNS
        .filter((c) => !(status === 'exited' && c.activeOnly))
        .map((c) => ({
            ...c,
            label: t(status === 'exited' && c.exitLabelKey ? c.exitLabelKey : c.labelKey),
        }));
    const rows = useMemo(() => sortInvestments(investments, sort), [investments, sort]);

    const toggleSort = (key) =>
        setSort((s) => (s.key === key
            ? { key, direction: s.direction === 'asc' ? 'desc' : 'asc' }
            : { key, direction: COLUMNS.find((c) => c.key === key)?.numeric ? 'desc' : 'asc' }));

    const toggleOpen = (key) => setOpen((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });

    const startEdit = (investment) => {
        setEditing(investment ? investment.id : 'new');
        setDraft(investment ? toDraft(investment) : emptyInvestment(status));
        setError(null);
    };
    const stopEdit = () => { setEditing(null); setDraft(null); setError(null); };

    // `?edit=<id>` opens that row — where a "needs input" elsewhere links to.
    const [params, setParams] = useSearchParams();
    const editParam = params.get('edit');
    useEffect(() => {
        if (!editParam) return;
        const target = investments.find((i) => String(i.id) === editParam);
        if (!target) return;
        startEdit(target);
        setParams((p) => { p.delete('edit'); return p; }, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editParam, investments]);

    const save = async () => {
        const parsed = fromDraft(draft);
        if (parsed.error) { setError(parsed.error); return; }
        const ok = await onSave(editing === 'new' ? null : editing, parsed.value);
        if (ok) stopEdit(); else setError('save');
    };

    const remove = async () => {
        if (!window.confirm(t('private.confirmDelete', { name: draft.name || '—' }))) return;
        if (await onDelete(editing)) stopEdit();
    };

    const editor = (onDeleteHandler) => (
        <Editor
            draft={draft}
            setDraft={setDraft}
            columns={columns}
            onSave={save}
            onCancel={stopEdit}
            onDelete={onDeleteHandler}
            busy={busy}
            error={error}
        />
    );

    const cell = (c, inv) => {
        const rounds = inv.rounds || [];
        const distributions = inv.distributions || [];
        switch (c.key) {
            case 'name':
                if ((inv.holdings || []).length > 0) {
                    const key = `${inv.id}:holdings`;
                    return (
                        <td key={c.key} className="is-left private__name">
                            {inv.name}
                            <button
                                type="button"
                                className="private__rounds_toggle private__holdings_toggle"
                                aria-expanded={open.has(key)}
                                onClick={() => toggleOpen(key)}
                            >
                                {tn('private.holdings', inv.holdings.length)} {open.has(key) ? '▴' : '▾'}
                            </button>
                        </td>
                    );
                }
                return <td key={c.key} className="is-left private__name">{inv.name}</td>;
            case 'kind': return <td key={c.key} className="is-left">{t(`private.kinds.${inv.kind}`)}</td>;
            case 'sector': return <td key={c.key} className="is-left">{sectorLabel(inv) ?? <span className="positions__empty">—</span>}</td>;
            case 'invested':
                if (inv.linkedAccountId && rounds.length === 0) {
                    return (
                        <td key={c.key} className="is-right">
                            <button
                                type="button"
                                className="ssum__needs ssum__needs_btn"
                                onClick={() => startEdit(inv)}
                                disabled={editing !== null}
                            >
                                {t('sections.needsInput')}
                            </button>
                        </td>
                    );
                }
                return (
                    <td key={c.key} className="is-right">
                        <TotalWithEntries
                            titleKey={ENTRY_LISTS.rounds.titleKey}
                            total={invested(inv)}
                            count={rounds.length}
                            expanded={open.has(`${inv.id}:rounds`)}
                            onToggle={() => toggleOpen(`${inv.id}:rounds`)}
                        />
                    </td>
                );
            case 'distributed':
                return (
                    <td key={c.key} className="is-right">
                        <TotalWithEntries
                            titleKey={ENTRY_LISTS.distributions.titleKey}
                            total={distributed(inv)}
                            count={distributions.length}
                            expanded={open.has(`${inv.id}:distributions`)}
                            onToggle={() => toggleOpen(`${inv.id}:distributions`)}
                        />
                    </td>
                );
            case 'currentMark':
                return (
                    <td key={c.key} className="is-right">
                        <MarkValue investment={inv} />
                        {inv.linkedAccountId && <span className="acct__tag private__live">{t('private.live')}</span>}
                    </td>
                );
            case 'pnl': return <PnlCell key={c.key} pnl={pnlOf(inv)} pct={pnlPctOf(inv)} />;
            case 'comments': return <td key={c.key} className="is-left private__comments">{inv.comments}</td>;
            default: return null;
        }
    };

    return (
        <>
            <div className="positions__controls">
                <div className="positions__count">{tn('private.count', investments.length)}</div>
                <button
                    type="button"
                    className="positions__filter"
                    onClick={() => startEdit(null)}
                    disabled={editing !== null}
                >
                    {t('private.add')}
                </button>
            </div>
            <div className="positions__scroll">
                <table className="positions private">
                    <thead>
                        <tr>
                            {columns.map((c) => (
                                <th key={c.key} className={`is-${c.align}${sort.key === c.key ? ' is-sorted' : ''}`}>
                                    <button type="button" onClick={() => toggleSort(c.key)}>
                                        {c.label}
                                        {sort.key === c.key && (
                                            <span aria-hidden="true">{sort.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                                        )}
                                    </button>
                                </th>
                            ))}
                            <th aria-label={t('private.col.actions')} />
                        </tr>
                    </thead>
                    <tbody>
                        {editing === 'new' && editor(null)}
                        {rows.map((inv) => {
                            if (editing === inv.id) {
                                return <React.Fragment key={inv.id}>{editor(remove)}</React.Fragment>;
                            }
                            const showRounds = open.has(`${inv.id}:rounds`) && (inv.rounds || []).length > 0;
                            const showDistributions = open.has(`${inv.id}:distributions`) && (inv.distributions || []).length > 0;
                            const showHoldings = open.has(`${inv.id}:holdings`) && (inv.holdings || []).length > 0;
                            return (
                                <React.Fragment key={inv.id}>
                                    <tr className="private__row">
                                        {columns.map((c) => cell(c, inv))}
                                        <td className="private__actions">
                                            <button
                                                type="button"
                                                className="private__edit"
                                                onClick={() => startEdit(inv)}
                                                disabled={editing !== null}
                                                aria-label={t('private.editAria', { name: inv.name })}
                                                title={t('private.edit')}
                                            >
                                                <PencilIcon />
                                            </button>
                                            <button
                                                type="button"
                                                className="notes__link"
                                                onClick={() => onMove(inv.id, status === 'active' ? 'exited' : 'active')}
                                                disabled={busy || editing !== null}
                                                aria-label={t(status === 'active' ? 'private.toExitsAria' : 'private.toActiveAria', { name: inv.name })}
                                            >
                                                {t(status === 'active' ? 'private.toExits' : 'private.toActive')}
                                            </button>
                                        </td>
                                    </tr>
                                    {showHoldings && (
                                        <tr className="private__detail">
                                            <td colSpan={columns.length + 1}>
                                                <PositionsTable positions={inv.holdings} baseCurrency={baseCurrency} />
                                            </td>
                                        </tr>
                                    )}
                                    {(showRounds || showDistributions) && (
                                        <tr className="private__detail">
                                            <td colSpan={columns.length + 1}>
                                                {showRounds && <Entries list="rounds" entries={inv.rounds} />}
                                                {showDistributions && <Entries list="distributions" entries={inv.distributions} />}
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                        {rows.length === 0 && editing !== 'new' && (
                            <tr>
                                <td colSpan={columns.length + 1} className="is-left positions__empty">
                                    {t(status === 'active' ? 'private.emptyActive' : 'private.emptyExits')}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </>
    );
}
